/**
 * connectionWarmService
 *
 * Warms the network path to the video CDN WITHOUT downloading any video bytes.
 *
 * Best practice for hover-to-play previews: keep `preload="none"` on the tiles
 * (so nothing loads until the user actually hovers), but eagerly open the
 * DNS + TCP + TLS handshake to the CDN origin(s) up front via `<link
 * rel="preconnect">`. When the first hover fires, the connection is already
 * established, so the browser skips straight to fetching the clip — removing
 * the cold-connection latency (often 100–300 ms) that made previews feel slow.
 *
 * - Zero video bytes are fetched — this only reserves a warm socket, so it does
 *   NOT reintroduce the per-tile CDN load we deliberately removed.
 * - Idempotent: each origin gets at most one <link> tag for the page lifetime.
 * - `dns-prefetch` is added alongside as a fallback for browsers that ignore
 *   `preconnect`.
 */

const warmedOrigins = new Set<string>();

function addLink(rel: string, href: string): void {
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  // Media is credential-less (public CDN); crossorigin lets the warm socket be
  // reused for the actual <video> request, which is also anonymous.
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

/**
 * Given a list of media URLs, preconnect to each distinct cross-origin host.
 * Safe to call repeatedly — already-warmed origins are skipped.
 */
export function warmVideoOrigins(urls: (string | undefined)[]): void {
  for (const url of urls) {
    if (!url) continue;
    let origin: string;
    try {
      origin = new URL(url).origin;
    } catch {
      continue; // malformed URL — nothing to warm
    }
    // Same-origin as the page needs no preconnect (already connected)
    if (origin === window.location.origin) continue;
    if (warmedOrigins.has(origin)) continue;

    warmedOrigins.add(origin);
    addLink('preconnect', origin);
    addLink('dns-prefetch', origin);
  }
}
